// Primitives: Reveal, Placeholder, CoffeeArt variants, Nav, Footer, Logo.

import { useEffect, useState } from 'react';
import { useContent, useLang, TONE_BG, UI } from './content.jsx';

/* ============== Reveal ============== */

export function useRevealObserver() {
  useEffect(() => {
    const revealAll = () => document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
    if (!('IntersectionObserver' in window)) { revealAll(); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    const scan = () => document.querySelectorAll('.reveal:not(.in)').forEach(el => io.observe(el));
    scan();
    const t1 = setTimeout(scan, 50);
    const t2 = setTimeout(scan, 300);
    const t3 = setTimeout(revealAll, 1200);
    return () => { io.disconnect(); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);
}

export function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const d = ['', 'reveal-d1', 'reveal-d2', 'reveal-d3', 'reveal-d4'][delay] || '';
  return <Tag className={`reveal ${d} ${className}`} {...rest}>{children}</Tag>;
}

/* ============== Placeholder + CoffeeArt ============== */

export function Placeholder({ tone = 'cream', label = 'IMG', ratio = '4/5', className = '', children, rounded = 'rounded-3xl' }) {
  const bg = TONE_BG[tone] || TONE_BG.cream;
  return (
    <div className={`relative overflow-hidden ${rounded} ${className}`} style={{ aspectRatio: ratio, background: bg }}>
      <CoffeeArt label={label} tone={tone}/>
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"/>
      <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90 bg-black/25 backdrop-blur-sm px-2 py-1 rounded-full nosel">
        {label}
      </div>
      {children}
    </div>
  );
}

export function CoffeeArt({ label = '', tone = 'cream' }) {
  const L = String(label).toUpperCase();
  let kind = 'cup';
  if (/CROISSANT|BAKE|BUTTER|OVEN|BUN|SOURDOUGH|FOCACCIA|DANISH|ROLL|KNOT/.test(L)) kind = 'croissant';
  else if (/CHEESECAKE|BASQUE|TIRAMISU|RASPBERRY|ECLAIR|DESSERT|CACAO|CLOUD|MUHALLEBI|TRES|MOLTEN/.test(L)) kind = 'dessert';
  else if (/REEL|LIFESTYLE|STORY|MORNING|GOLDEN|MATCHA/.test(L)) kind = 'mood';
  else if (/ROSE|LATTE|MOCHA|COFFEE|ESPRESSO|CUP|AMERICANO|CAPPUCCINO|FLAT|CORTADO|BREW|TONIC|AFFOGATO/.test(L)) kind = 'cup';
  else if (tone === 'butter') kind = 'croissant';
  else if (tone === 'cocoa')  kind = 'dessert';
  return <ArtScene kind={kind} tone={tone}/>;
}

export function ArtScene({ kind, tone }) {
  if (kind === 'croissant') return <CroissantArt tone={tone}/>;
  if (kind === 'dessert')   return <DessertArt tone={tone}/>;
  if (kind === 'mood')      return <MoodArt tone={tone}/>;
  return <CupArt tone={tone}/>;
}

function CupArt({ tone }) {
  const coffee = tone === 'rose' ? '#8b4a3a' : tone === 'pistachio' ? '#6a5a2a' : '#4a2e1e';
  const crema  = tone === 'rose' ? '#d9967f' : tone === 'pistachio' ? '#b8ac76' : '#a67548';
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%),
                     radial-gradient(ellipse at 80% 90%, rgba(74,46,30,0.25) 0%, rgba(74,46,30,0) 60%)`,
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '58%', transform: 'translate(-50%,-50%)',
        width: '86%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 40% 35%, #ffffff 0%, #f5ebdb 70%, #d8c3a0 100%)',
        boxShadow: '0 16px 30px rgba(74,46,30,0.2), inset 0 0 30px rgba(139,90,60,0.1)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '58%', transform: 'translate(-50%,-50%)',
        width: '62%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 28%, #ffffff 0%, #f8f1e1 70%, #e5d3b4 100%)',
        boxShadow: 'inset 0 0 22px rgba(139,90,60,0.25)',
      }}/>
      <div className="absolute overflow-hidden" style={{
        left: '50%', top: '58%', transform: 'translate(-50%,-50%)',
        width: '54%', aspectRatio: '1', borderRadius: '50%',
        background: `radial-gradient(circle at 38% 28%, ${crema} 0%, ${coffee} 60%, #2a1a10 100%)`,
        boxShadow: 'inset 0 0 40px rgba(0,0,0,0.5)',
      }}>
        <svg viewBox="-100 -100 200 200" className="absolute inset-0 w-full h-full">
          <path d="M 0 40 C -32 10, -46 -20, -22 -30 C -6 -34, 0 -20, 0 -8 C 0 -20, 6 -34, 22 -30 C 46 -20, 32 10, 0 40 Z"
            fill="#f5ebd6" opacity="0.9"/>
        </svg>
        <div className="absolute" style={{
          left: '22%', top: '14%', width: '42%', height: '28%',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(255,245,228,0.45) 0%, rgba(255,245,228,0) 70%)',
        }}/>
      </div>
      <div className="absolute" style={{
        left: '50%', top: '18%', width: '30%', height: '30%',
        transform: 'translateX(-50%)',
        background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 70%)',
        filter: 'blur(10px)',
      }}/>
      <div className="absolute inset-0">
        {[[8,76,-20],[86,68,30],[12,10,45],[88,14,-30]].map(([x,y,r],i)=>(
          <div key={i} className="absolute" style={{
            left: x+'%', top: y+'%', width: '7%', aspectRatio: '1',
            transform: `rotate(${r}deg)`,
          }}>
            <svg viewBox="0 0 40 56" className="w-full h-full">
              <ellipse cx="20" cy="28" rx="12" ry="22" fill="#2a1a10"/>
              <path d="M20 8 C 14 20, 14 36, 20 48" stroke="#000" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6"/>
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

function CroissantArt() {
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 55%), radial-gradient(ellipse at 80% 85%, rgba(139,90,60,0.3) 0%, rgba(139,90,60,0) 60%)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '58%', transform: 'translate(-50%,-50%) rotate(-6deg)',
        width: '94%', height: '62%', borderRadius: '14%/24%',
        background: 'linear-gradient(100deg, #c9a575 0%, #8b5a3c 100%)',
        boxShadow: '0 16px 30px rgba(74,46,30,0.3)',
      }}/>
      <svg viewBox="0 0 200 200" className="absolute" style={{
        left: '50%', top: '55%', transform: 'translate(-50%,-50%) rotate(-10deg)',
        width: '75%', height: '75%',
      }}>
        <defs>
          <radialGradient id="cr" cx="0.5" cy="0.5" r="0.6">
            <stop offset="0"   stopColor="#f0c775"/>
            <stop offset="0.6" stopColor="#c8893a"/>
            <stop offset="1"   stopColor="#6b4024"/>
          </radialGradient>
        </defs>
        <path d="M 30 110 C 40 50, 80 35, 100 55 C 120 35, 160 50, 170 110 C 160 145, 120 165, 100 148 C 80 165, 40 145, 30 110 Z"
          fill="url(#cr)" stroke="#4a2e1e" strokeWidth="1.2"/>
        {[0,1,2,3,4,5].map(i => {
          const t = i/5;
          return (
            <path key={i}
              d={`M ${40 + t*14} ${108 - t*8} Q 100 ${50 + t*18}, ${160 - t*14} ${108 - t*8}`}
              fill="none" stroke="#6b4024" strokeWidth="1.2" opacity={0.5 - t*0.05}/>
          );
        })}
        <path d="M 50 90 Q 100 60 150 90" fill="none" stroke="#fbe7b0" strokeWidth="2" opacity="0.55"/>
      </svg>
      <div className="absolute" style={{
        left: '18%', top: '74%', width: '12%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 40% 35%, #fff4c6 0%, #f0c775 80%)',
        boxShadow: '0 6px 12px rgba(74,46,30,0.2)',
      }}/>
      <svg viewBox="0 0 120 40" className="absolute" style={{ right: '6%', bottom: '12%', width: '28%' }}>
        <path d="M 6 20 C 30 8, 70 8, 114 20" stroke="#6b7a3a" strokeWidth="1.6" fill="none"/>
        {[0.15,0.3,0.45,0.6,0.75,0.9].map((t,i)=>(
          <ellipse key={i} cx={6+t*108} cy={20 - Math.sin(t*Math.PI)*4 - 3} rx="5" ry="2.4" fill="#8a9b4a"/>
        ))}
      </svg>
    </div>
  );
}

function DessertArt({ tone }) {
  const topCol = tone === 'rose' ? '#e9b7a6' : tone === 'pistachio' ? '#a6b38a' : '#6b4024';
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 55%)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '60%', transform: 'translate(-50%,-50%)',
        width: '82%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 40% 30%, #ffffff 0%, #f5ebdb 70%, #d8c3a0 100%)',
        boxShadow: '0 16px 30px rgba(74,46,30,0.22)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '56%', transform: 'translate(-50%,-50%)',
        width: '58%', height: '46%', borderRadius: '14% 14% 10% 10% / 22% 22% 10% 10%',
        background: 'linear-gradient(180deg, #fff4de 0%, #f0d9b0 60%, #d8a56e 100%)',
        boxShadow: 'inset 0 -14px 24px rgba(139,90,60,0.3), 0 8px 18px rgba(74,46,30,0.2)',
      }}>
        <div className="absolute top-0 inset-x-0" style={{
          height: '32%',
          background: `linear-gradient(180deg, ${topCol} 0%, rgba(0,0,0,0.1) 100%)`,
          borderRadius: '14% 14% 0 0 / 32% 32% 0 0',
          boxShadow: 'inset 0 4px 10px rgba(255,255,255,0.4)',
        }}/>
        <div className="absolute top-[28%] left-[18%]" style={{
          width: '18%', height: '22%',
          background: topCol, borderRadius: '0 0 50% 50%', opacity: 0.9,
        }}/>
        <div className="absolute top-[28%] right-[22%]" style={{
          width: '14%', height: '18%',
          background: topCol, borderRadius: '0 0 50% 50%', opacity: 0.9,
        }}/>
      </div>
      <div className="absolute" style={{
        right: '22%', top: '30%', width: '10%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #f9bfb0 0%, #c14a4a 80%)',
        boxShadow: '0 4px 10px rgba(74,46,30,0.25)',
      }}/>
      <div className="absolute" style={{
        right: '6%', bottom: '14%', width: '30%', height: '3%',
        transform: 'rotate(18deg)', borderRadius: 999,
        background: 'linear-gradient(90deg, #d9c9ab 0%, #8b7a5a 100%)',
        boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
      }}/>
    </div>
  );
}

function MoodArt({ tone }) {
  const c1 = tone === 'rose' ? '#f5dcd4' : tone === 'butter' ? '#fff1d6' : tone === 'pistachio' ? '#d9dfb6' : '#ede0ce';
  const c2 = tone === 'rose' ? '#e9b7a6' : tone === 'butter' ? '#e4c79a' : tone === 'pistachio' ? '#a6b38a' : '#c9a575';
  return (
    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}>
      <div className="absolute" style={{
        left: '-20%', top: '-20%', width: '90%', height: '90%',
        background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 60%)',
        filter: 'blur(20px)',
      }}/>
      <div className="absolute inset-0 opacity-25" style={{
        backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.4) 0 1px, rgba(255,255,255,0) 1px 3px), repeating-linear-gradient(0deg, rgba(74,46,30,0.06) 0 1px, rgba(74,46,30,0) 1px 3px)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '56%', transform: 'translate(-50%,-50%)',
        width: '48%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 40% 30%, #ffffff 0%, #f5ebdb 70%, #d8c3a0 100%)',
        boxShadow: '0 18px 30px rgba(74,46,30,0.22)',
      }}/>
      <div className="absolute" style={{
        left: '50%', top: '56%', transform: 'translate(-50%,-50%)',
        width: '36%', aspectRatio: '1', borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 28%, #a67548 0%, #4a2e1e 100%)',
        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.4)',
      }}>
        <svg viewBox="-100 -100 200 200" className="absolute inset-0 w-full h-full">
          <path d="M 0 40 C -28 10, -40 -18, -20 -28 C -6 -30, 0 -18, 0 -8 C 0 -18, 6 -30, 20 -28 C 40 -18, 28 10, 0 40 Z"
            fill="#f5ebd6" opacity="0.9"/>
        </svg>
      </div>
      <svg viewBox="0 0 120 40" className="absolute" style={{ left: '6%', bottom: '10%', width: '34%' }}>
        <path d="M 6 20 C 30 8, 70 8, 114 20" stroke="#6b7a3a" strokeWidth="1.6" fill="none"/>
        {[0.15,0.3,0.45,0.6,0.75,0.9].map((t,i)=>(
          <ellipse key={i} cx={6+t*108} cy={20 - Math.sin(t*Math.PI)*4 - 3} rx="5" ry="2.4" fill="#8a9b4a"/>
        ))}
      </svg>
    </div>
  );
}

/* ============== Pulse ============== */

export function PulseDot({ color = '#c9a575' }) {
  return (
    <span className="relative inline-flex h-2 w-2 mr-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: color }}/>
      <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }}/>
    </span>
  );
}

// Footer column items can be either {tr, en} (legacy) or {label: {tr, en}, link}
function normalizeFooterItem(raw) {
  if (raw && typeof raw === 'object' && 'label' in raw) {
    return { label: raw.label || { tr: '', en: '' }, link: raw.link || '' };
  }
  return { label: raw || { tr: '', en: '' }, link: '' };
}

/* ============== Legal page overlay ============== */

export function LegalPage({ pageKey, onClose }) {
  const c = useContent();
  const { t, lang } = useLang();
  const page = c.legal?.[pageKey];
  const updated = c.legal?.updated;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!page) return null;

  const updatedLabel = lang === 'tr'
    ? `Son güncelleme: ${new Date(updated || Date.now()).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}`
    : `Last updated: ${new Date(updated || Date.now()).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })}`;

  const body = String(t(page.body) || '');
  const blocks = body.split(/\n\n+/);

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-cream" data-lenis-prevent>
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-cream/85 border-b border-espresso/10">
        <div className="mx-auto max-w-3xl px-6 h-16 flex items-center justify-between">
          <button onClick={onClose} className="inline-flex items-center gap-2 text-[13px] text-espresso hover:text-cocoa transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            {lang === 'tr' ? 'Geri' : 'Back'}
          </button>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-tan">Glamm · Legal</div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-espresso text-cream grid place-items-center hover:bg-ink transition">✕</button>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-6 py-14 md:py-20">
        <div className="font-mono text-[10.5px] tracking-[0.26em] uppercase text-tan">— Glamm</div>
        <h1 className="font-display text-[42px] md:text-[54px] leading-[1.02] tracking-[-0.02em] text-espresso mt-3">
          {t(page.title)}
        </h1>
        <div className="mt-3 text-[12px] font-mono text-espresso/55">{updatedLabel}</div>

        <div className="mt-10 space-y-6 text-[15px] leading-[1.75] text-espresso/85">
          {blocks.map((block, i) => {
            const lines = block.split('\n');
            if (lines[0].startsWith('## ')) {
              const heading = lines[0].replace(/^##\s+/, '');
              const rest = lines.slice(1).join('\n');
              return (
                <section key={i}>
                  <h2 className="font-display text-[22px] md:text-[24px] text-espresso tracking-tight mt-2 mb-3">{heading}</h2>
                  <LegalText text={rest}/>
                </section>
              );
            }
            return <LegalText key={i} text={block}/>;
          })}
        </div>

      </article>
    </div>
  );
}

function LegalText({ text }) {
  const lines = text.split('\n').filter(l => l.trim());
  const bulletLines = lines.every(l => l.startsWith('• '));
  if (bulletLines) {
    return (
      <ul className="space-y-1.5 pl-0 list-none">
        {lines.map((l, j) => (
          <li key={j} className="flex gap-3">
            <span className="text-tan mt-[2px]">•</span>
            <span>{l.replace(/^•\s+/, '')}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="whitespace-pre-line">{text}</p>;
}

/* ============== Logo ============== */

export function Logo({ size = 56, showText = true, dark = false }) {
  const c = useContent();
  const { t } = useLang();
  const brand = c.brand;
  const textSize = Math.round(size * 0.44);
  const tagSize = Math.max(9, Math.round(size * 0.18));
  return (
    <a href="#top" className="flex items-center gap-3 nosel group shrink-0">
      <span className="relative inline-grid place-items-center shrink-0"
        style={{ width: size, height: size }}>
        <span aria-hidden className="absolute -inset-1 rounded-[20px] blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-500"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(201,165,117,.55), transparent 70%)' }}/>
        <img src={brand.logo || '/assets/glamm.png'} alt={t(brand.name)} loading="eager"
          className="relative rounded-[16px] bg-[#faf5ee] ring-1 ring-espresso/15 shadow-[0_6px_20px_rgba(74,46,30,0.18)] p-[2px] transition-transform duration-500 group-hover:scale-[1.04] object-contain"
          style={{ width: size, height: size }}/>
      </span>
      {showText && (
        <div className="leading-none">
          <div className={`font-display font-semibold tracking-[-0.015em] ${dark ? 'text-cream' : 'text-espresso'}`}
            style={{ fontSize: textSize }}>{t(brand.name)}</div>
          <div className="font-mono tracking-[0.22em] uppercase text-tan mt-1.5"
            style={{ fontSize: tagSize }}>{t(brand.tagline)}</div>
        </div>
      )}
    </a>
  );
}

/* ============== Language Toggle ============== */

export function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-espresso/25 bg-white/85 shadow-sm p-0.5">
      {['tr', 'en'].map(l => (
        <button key={l} onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-[12.5px] font-mono tracking-[0.15em] uppercase rounded-full transition
            ${lang === l ? 'bg-espresso text-cream' : 'text-espresso/70 hover:text-espresso'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

/* ============== Nav ============== */

export function TopNav({ dark, setDark, onOpenStores, onOpenFullMenu }) {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', f, { passive: true });
    f();
    return () => window.removeEventListener('scroll', f);
  }, []);

  const links = [
    { href: '#stores',  label: t(UI.nav.stores) },
    { href: '#about',   label: t(UI.nav.about) },
    { href: '#contact', label: t(UI.nav.contact) },
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? 'py-3' : 'py-5'}`}>
      <div className="mx-auto max-w-7xl px-5">
        <div className={`flex items-center justify-between rounded-full px-4 md:px-5 py-2.5 transition-all duration-500 ${scrolled ? 'glass shadow-[0_8px_30px_rgba(74,46,30,0.08)]' : 'bg-transparent'}`}>
          <Logo size={56} dark={dark}/>
          <nav className="hidden md:flex items-center gap-8">
            {links.map(l => (
              <a key={l.label} href={l.href}
                onClick={l.onClick ? (e) => { e.preventDefault(); l.onClick(); } : undefined}
                className="text-[15px] font-medium tracking-wide text-espresso/85 hover:text-espresso transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <LangToggle/>
            <button onClick={() => setDark(!dark)} aria-label="Toggle dark mode"
              className="h-10 w-10 rounded-full grid place-items-center border border-espresso/25 bg-white/85 shadow-sm hover:bg-white transition">
              <span className="text-[17px] leading-none">{dark ? '☾' : '☀'}</span>
            </button>
            {onOpenFullMenu && (
              <button onClick={onOpenFullMenu}
                className="relative hidden sm:inline-flex items-center gap-2.5 rounded-full bg-espresso text-cream px-6 py-2.5 text-[14px] font-medium tracking-wide hover:bg-ink transition-all group overflow-hidden nav-menu-cta">
                <span aria-hidden className="nav-menu-halo"/>
                <span aria-hidden className="nav-menu-shine"/>
                <span className="relative inline-flex items-center gap-2.5">
                  <span className="nav-menu-dot"/>
                  {t(UI.nav.menu)}
                  <span className="inline-block transition-transform duration-500 group-hover:translate-x-0.5">→</span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export function BottomNav({ onOpenFullMenu }) {
  const { t } = useLang();
  const items = [
    { href: '#top',       label: t(UI.nav.home),    icon: 'home' },
    { href: '#stores',    label: t(UI.nav.stores),  icon: 'pin' },
    { href: '#/tam-menu', label: t(UI.nav.menu),    icon: 'menu', onClick: onOpenFullMenu, primary: true },
    { href: '#about',     label: t(UI.nav.about),   icon: 'user' },
    { href: '#contact',   label: t(UI.nav.contact), icon: 'bag' },
  ];
  const icon = (k) => {
    const common = { width: 20, height: 20, fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (k) {
      case 'home': return <svg {...common}><path d="M3 10.5L12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/></svg>;
      case 'menu': return <svg {...common}><path d="M4 7h16M4 12h16M4 17h10"/></svg>;
      case 'bag':  return <svg {...common}><path d="M5 7h14l-1 14H6L5 7z"/><path d="M9 7a3 3 0 1 1 6 0"/></svg>;
      case 'pin':  return <svg {...common}><path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="10" r="2.5"/></svg>;
      case 'user': return <svg {...common}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>;
      default: return null;
    }
  };
  return (
    <div className="md:hidden fixed bottom-4 inset-x-4 z-50">
      <div className="glass rounded-full shadow-[0_10px_40px_rgba(74,46,30,0.18)] px-2 py-2 flex items-center justify-between">
        {items.map((it, i) => (
          <a key={i} href={it.href}
            onClick={it.onClick ? (e) => { e.preventDefault(); it.onClick(); } : undefined}
            className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-full transition-colors
              ${it.primary ? 'bg-espresso text-cream mx-1' : 'text-espresso/80'}`}>
            {icon(it.icon)}
            <span className="text-[9px] tracking-wide">{it.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ============== Footer ============== */

export function Footer() {
  const c = useContent();
  const { t } = useLang();
  const f = c.footer;
  return (
    <footer className="relative mt-24 border-t border-espresso/10 bg-cream">
      <div className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <Logo size={72}/>
          <p className="text-[14.5px] text-espresso/75 mt-5 leading-relaxed max-w-[260px]">{t(f.blurb)}</p>
        </div>
        {f.cols.map((col, i) => (
          <div key={i}>
            <div className="font-display text-[20px] mb-4">{t(col.title)}</div>
            <ul className="space-y-2.5 text-[14.5px] text-espresso/80">
              {col.items.map((raw, j) => {
                const { label, link } = normalizeFooterItem(raw);
                const isExt = /^https?:\/\//i.test(link || '');
                return (
                  <li key={j}>
                    <a className="hover:text-espresso transition-colors"
                      href={link || '#'}
                      {...(isExt ? { target: '_blank', rel: 'noreferrer' } : {})}>
                      {t(label)}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div>
          <div className="font-display text-[20px] mb-4">{t({ tr: 'Bizi Takip Et', en: 'Follow Us' })}</div>
          <p className="text-[14px] text-espresso/75 mb-4">
            {t({ tr: 'Yeni sezon ve günlük lezzetler için Instagram\'da buluşalım.',
                 en: 'Catch new seasons and daily flavours on Instagram.' })}
          </p>
          <a href={c.instagram?.url || '#'} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-espresso text-cream px-5 py-2.5 text-[14px] font-medium tracking-wide hover:bg-ink transition">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
            {c.brand.instagram || '@glammcoffee'}
          </a>
          <div className="mt-5 text-[14px] text-espresso/75 space-y-1">
            <div>{c.brand.email}</div>
            <div>{c.brand.phone}</div>
          </div>
        </div>
      </div>
      <div className="border-t border-espresso/10">
        <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[13px] text-espresso/65">
          <div>{t(f.copyright).replace('{year}', new Date().getFullYear())}</div>
          <div className="flex gap-6">
            <a href="#/gizlilik" className="hover:text-espresso transition">{t(UI.footer.privacy)}</a>
            <a href="#/kvkk" className="hover:text-espresso transition">{t(UI.footer.kvkk)}</a>
            <a href="#/cerezler" className="hover:text-espresso transition">{t(UI.footer.cookies)}</a>
          </div>
        </div>
      </div>
      <div className="border-t border-espresso/10">
        <div className="mx-auto max-w-7xl px-6 py-4 text-center text-[12.5px] text-espresso/55 tracking-wide">
          Powered by{' '}
          <a href="https://stokzen.com" target="_blank" rel="noreferrer"
            className="font-semibold text-espresso/80 hover:text-espresso transition-colors">
            STOKZEN
          </a>
        </div>
      </div>
    </footer>
  );
}
